import React, { useState, useRef, useEffect } from "react";
import styles from "./TheElchemyAdvantage.module.scss";
import LocalPresenceSVG from "./LocalPresenceSVG";
import USBasedTeamSupportSVG from "./USBasedTeamSupportSVG";
import TechnologyDrivenOperationsSVG from "./TechnologyDrivenOperationsSVG.";
import CostEfficiencySVG from "./CostEfficiencySVG";
import RegulatoryComplianceSVG from "./RegulatoryComplianceSVG";
import QualityCommitmentSVG from "./QualityCommitmentSVG";
import Image from "next/image";
import { getCDNImageUrl } from "@/utils/Common";

const cardCombinations = [
  {
    id: 1,
    leftHeading: "Local Presence",
    leftIcon: LocalPresenceSVG,
    leftDescription:
      "With warehouses in New Jersey, Houston and other key regions, Elchemy is a dependable local partner for high quality chemical ingredients, enabling faster fulfillment and smoother operations.",
    rightHeading: "Support You Can Rely On",
    rightIcon: USBasedTeamSupportSVG,
    rightDescription:
      "Our dedicated support team is available around the clock to help with anything you need. Whether it’s a quick clarification or a detailed request, we are just a call away.",
    imageSrc: getCDNImageUrl("local-presence.webp"),
  },
  {
    id: 2,
    leftHeading: "Technology-driven Operations",
    leftIcon: TechnologyDrivenOperationsSVG,
    leftDescription:
      "Our platform offers real-time access to inventory, shipment tracking, and documentation in one place. Empower your teams with full visibility for faster decision-making",
    rightHeading: "Request Sample",
    rightIcon: CostEfficiencySVG,
    rightDescription:
      "Request product samples with complete confidence. All samples are quality-verified and dispatched quickly for delivery within 24–48 hours so you can test, validate and move forward without delay.",
    imageSrc: getCDNImageUrl("technology-driven-operations.webp"),
  },
  {
    id: 3,
    leftHeading: "Regulatory Compliance",
    leftIcon: RegulatoryComplianceSVG,
    leftDescription:
      "We go the extra mile to ensure ingredients are sourced ethically and sustainably. Every product is supported by complete documentation that meets U.S. and global standards, including Kosher, Halal",
    rightHeading: "Quality Commitment",
    rightIcon: QualityCommitmentSVG,
    rightDescription:
      "We partner with trusted, globally audited manufacturers. Every product is backed by a Certificate of Analysis, Safety Data Sheet, and traceable supply chain for consistency and safety",
    imageSrc: getCDNImageUrl("regulatory-compliance.webp"),
  },
];

function TheElchemyAdvantage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const sliderRef = useRef(null);

  // Minimum swipe distance required to trigger slide change
  const minSwipeDistance = 50;

  // Create a flat array of slides for mobile: each card (left/right) is a separate slide
  const mobileSlides = cardCombinations.flatMap((card) => [
    {
      id: `${card.id}-left`,
      heading: card.leftHeading,
      icon: card.leftIcon,
      description: card.leftDescription,
    },
    {
      id: `${card.id}-right`,
      heading: card.rightHeading,
      icon: card.rightIcon,
      description: card.rightDescription,
    },
  ]);

  const handleDotClick = (index) => {
    setCurrentSlide(index);
  };

  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && currentSlide < mobileSlides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
    if (isRightSwipe && currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  return (
    <div className={styles.theElchemyAdvantage}>
      {/* Small Heading */}
      <h6>VISIONARY STEPS</h6>
      {/* Big Heading */}
      <h2>The Elchemy Advantage</h2>

      {/* Desktop Card Combinations - Column layout */}
      <div className={styles.desktopCardCombinations}>
        {cardCombinations.map((card) => {
          return (
            <div key={card.id} className={styles.desktopCardRow}>
              {/* First Card */}
              <div className={styles.desktopCardOne}>
                {/* Left Section */}
                <div className={styles.desktopCardOneLeftSection}>
                  {/* Icon with left SVG */}
                  {card.leftIcon()}

                  <div>
                    <h3 className={styles.desktopCardHeading}>
                      {card.leftHeading}
                    </h3>
                    <p className={styles.desktopCardDescription}>
                      {card.leftDescription}
                    </p>
                  </div>
                </div>
                {/* Right Section - Image */}
                <div className={styles.desktopCardOneRightSection}>
                  <Image
                    className={styles.desktopCardImage}
                    src={card.imageSrc}
                    alt={card.leftHeading}
                    fill
                  />
                </div>
              </div>
              {/* Second Card */}
              <div className={styles.desktopCardTwo}>
                {/* Icon with Right SVG */}

                {card.rightIcon()}

                <div>
                  <h3 className={styles.desktopCardHeading}>
                    {card.rightHeading}
                  </h3>
                  <p className={styles.desktopCardDescription}>
                    {card.rightDescription}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Mobile Carousel */}
      <div className={styles.mobileCarousel}>
        <div className={styles.mobileCarouselContainer}>
          <div
            ref={sliderRef}
            className={styles.mobileCarouselSlider}
            style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {mobileSlides.map((slide, index) => (
              <div key={slide.id} className={styles.mobileCarouselSlide}>
                <div
                  style={
                    index % 2 == 0
                      ? { backgroundColor: "#F6F6F6" }
                      : { backgroundColor: "#FFF8F8" }
                  }
                  className={styles.mobileCarouselCard}
                >
                  <div className={styles.mobileCardSVGContainer}>
                    {slide.icon()}
                  </div>
                  <div>
                    <h3 className={styles.mobileCardHeading}>
                      {slide.heading}
                    </h3>
                    <p className={styles.mobileCardDescription}>
                      {slide.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pagination Dots */}
        <div className={styles.paginationContainer}>
          {mobileSlides.map((_, index) => (
            <button
              key={index}
              className={`${styles.paginationDot} ${
                index === currentSlide ? styles.paginationDotActive : ""
              }`}
              onClick={() => handleDotClick(index)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default TheElchemyAdvantage;
